import React, { useCallback, useEffect, useMemo, useState } from "react";
import { axiosMaster } from "../../api/axiosInstance";
import { FaFileExcel, FaSearch, FaUserTimes, FaStore, FaPhone, FaCalendarAlt } from "react-icons/fa";
import { useAuth } from "../../auth/AuthContext";

/* ═══════════════════════════════════════════════════
   DATE UTILITY — YYYYMMDD integer, no timezone issues
   ═══════════════════════════════════════════════════ */
const toDateInt = (str) => {
  if (!str) return null;
  const s = String(str);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return parseInt(s.slice(0, 10).replace(/-/g, ""), 10);
  if (/^\d{2}\/\d{2}\/\d{4}/.test(s)) {
    const [dd, mm, yyyy] = s.split("/");
    return parseInt(`${yyyy}${mm}${dd}`, 10);
  }
  return null;
};

/* ─── Format ISO date → DD/MM/YYYY for display ─── */
const formatDate = (str) => {
  if (!str) return "-";
  const s = String(str);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const [yyyy, mm, dd] = s.slice(0, 10).split("-");
    return `${dd}/${mm}/${yyyy}`;
  }
  return s;
};

/* ═══════════════════════════════════════════════════
   CSV ESCAPE HELPER
   ═══════════════════════════════════════════════════ */
const escapeCSV = (value) => {
  if (value === null || value === undefined) return "";
  const str = String(value);
  const safe = /^[=+\-@]/.test(str) ? `'${str}` : str;
  if (safe.includes(",") || safe.includes('"') || safe.includes("\n")) {
    return `"${safe.replace(/"/g, '""')}"`;
  }
  return safe;
};

/* ═══════════════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════════════ */
const LeavingEmployeeReport = () => {

  /* ─── AUTH ─── */
  const { user } = useAuth();
  const groupId      = user?.groupId;
  const userTypeId   = user?.userTypeId;
  const restaurantId = user?.restaurantId;

  /* ─── STATE ─── */
  const today = new Date().toISOString().split("T")[0];
  const firstOfMonth = today.slice(0, 7) + "-01";

  const [fromDate,      setFromDate]      = useState(firstOfMonth);
  const [toDate,        setToDate]        = useState(today);
  const [employees,     setEmployees]     = useState([]);
  const [restaurants,   setRestaurants]   = useState([]);
  const [loading,       setLoading]       = useState(false);
  const [search,        setSearch]        = useState("");
  const [selectedRest,  setSelectedRest]  = useState("ALL");

  /* ─── FETCH ─── */
  const fetchData = useCallback(async () => {
    if (!groupId) return;
    try {
      setLoading(true);
      const [empRes, restRes] = await Promise.all([
        axiosMaster.get(`/EmployeeMaster/GetByGroup?groupId=${groupId}`),
        axiosMaster.get(`/RestaurantMaster/GetByGroup?groupId=${groupId}`),
      ]);
      setEmployees(empRes.data || []);
      setRestaurants(restRes.data || []);
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [groupId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  /* ─── FILTER: employees who LEFT within the date range ─── */
  const leavingEmployees = useMemo(() => {
    const from = toDateInt(fromDate);
    const to   = toDateInt(toDate);

    return employees.filter((emp) => {
      const relieve = toDateInt(emp.relievingDate);

      // Must have a relievingDate (left the company)
      if (!relieve) return false;

      // relievingDate must fall within selected range
      if (relieve < from || relieve > to) return false;

      // userTypeId 4 = restaurant-level user → filter by their restaurant
      if (userTypeId === 4 && restaurantId) {
        return emp.activeRestId === restaurantId || emp.restId === restaurantId;
      }

      return true;
    });
  }, [employees, fromDate, toDate, userTypeId, restaurantId]);

  /* ─── RESTAURANT MAP ─── */
  const restMap = useMemo(() => {
    const map = {};
    restaurants.forEach((r) => { map[r.restId] = r.restaurantName; });
    return map;
  }, [restaurants]);

  /* ─── RESTAURANT OPTIONS for dropdown ─── */
  const restaurantOptions = useMemo(() => {
    const opts = [{ value: "ALL", label: "All Restaurants" }];
    restaurants.forEach((r) => {
      opts.push({ value: String(r.restId), label: r.restaurantName });
    });
    return opts;
  }, [restaurants]);

  /* ─── SEARCH + RESTAURANT FILTER ─── */
  const filteredData = useMemo(() => {
    let list = leavingEmployees;

    if (selectedRest !== "ALL") {
      const rid = Number(selectedRest); // select always returns string → convert
      list = list.filter(
        (emp) => Number(emp.activeRestId) === rid || Number(emp.restId) === rid
      );
    }

    if (search.trim()) {
      const term = search.toLowerCase();
      list = list.filter(
        (emp) =>
          emp.empName?.toLowerCase().includes(term) ||
          String(emp.empId).includes(term) ||
          emp.empMobileNo?.includes(term)
      );
    }

    return list;
  }, [leavingEmployees, search, selectedRest]);

  /* ─── EXPORT ─── */
  const exportExcel = () => {
    if (!filteredData.length) return;

    const rows = filteredData.map((emp) => ({
      EmpId:         emp.empId,
      Name:          emp.empName || "",
      Mobile:        emp.empMobileNo || "",
      Restaurant:    restMap[emp.activeRestId || emp.restId] || "",
      JoiningDate:   formatDate(emp.joiningDate),
      RelievingDate: formatDate(emp.relievingDate),
      EmpType:       emp.empType || "",
      EmpStatus:     emp.empStatus || "",
    }));

    const csv =
      "data:text/csv;charset=utf-8," +
      [
        Object.keys(rows[0]).join(","),
        ...rows.map((row) => Object.values(row).map(escapeCSV).join(",")),
      ].join("\n");

    const link = document.createElement("a");
    link.href     = encodeURI(csv);
    link.download = `LeavingEmployees_${fromDate}_to_${toDate}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  /* ═══════════════════════════════════════════════════
     RENDER
     ═══════════════════════════════════════════════════ */
  return (
    <div className="bg-white p-4 md:p-5 rounded-xl shadow border">

      {/* ── HEADER ── */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">

        <div>
          <h2 className="text-lg md:text-xl font-bold text-slate-800 flex items-center gap-2">
            <FaUserTimes className="text-red-500" />
            Leaving Employee Report
          </h2>
          <p className="text-xs text-gray-400 mt-0.5">
            Employees relieved within the selected date range
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">

          {/* FROM DATE */}
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="border px-2 py-1.5 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          />

          {/* TO DATE */}
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="border px-2 py-1.5 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          />

          {/* RESTAURANT FILTER (group-level users only) */}
          {userTypeId !== 4 && (
            <select
              value={selectedRest}
              onChange={(e) => setSelectedRest(e.target.value)}
              className="border px-2 py-1.5 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            >
              {restaurantOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          )}

          {/* SEARCH */}
          <div className="relative">
            <FaSearch className="absolute left-2.5 top-2.5 text-gray-400 text-xs" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              className="pl-8 pr-2 py-1.5 border rounded-md text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>

          {/* EXPORT */}
          <button
            onClick={exportExcel}
            disabled={!filteredData.length}
            className="bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-3 py-1.5 rounded-md text-sm flex items-center justify-center gap-2"
          >
            <FaFileExcel /> Export
          </button>

        </div>
      </div>

      {/* ── SUMMARY CARD ── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
          <p className="text-xs text-blue-500 font-medium">Total Employees</p>
          <p className="text-2xl font-bold text-blue-600">{employees.filter(e => !e.relievingDate).length}</p>
        </div>

        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
          <p className="text-xs text-red-500 font-medium">Total Leaving</p>
          <p className="text-2xl font-bold text-red-600">{filteredData.length}</p>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
          <p className="text-xs text-gray-500 font-medium">Date Range</p>
          <p className="text-sm font-semibold text-gray-600">
            {formatDate(fromDate)} → {formatDate(toDate)}
          </p>
        </div>

      </div>

      {/* ── DESKTOP TABLE ── */}
      <div className="hidden md:block border rounded-lg max-h-[500px] overflow-auto">
        <table className="w-full text-xs border-collapse">

          <thead className="bg-gray-200 sticky top-0">
            <tr>
              <th className="border p-2 text-center">ID</th>
              <th className="border p-2 text-left">Name</th>
              <th className="border p-2 text-left">Mobile</th>
              <th className="border p-2 text-left">Restaurant</th>
              <th className="border p-2 text-center">Joining Date</th>
              <th className="border p-2 text-center">Relieving Date</th>
              <th className="border p-2 text-center">Type</th>
              <th className="border p-2 text-center">Status</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr><td colSpan="8" className="text-center p-5 text-gray-500">Loading...</td></tr>
            ) : filteredData.length === 0 ? (
              <tr><td colSpan="8" className="text-center p-5 text-gray-400">No leaving employees found for this range.</td></tr>
            ) : filteredData.map((emp) => (
              <tr key={emp.empId} className="hover:bg-red-50 bg-white">
                <td className="border p-2 text-center">{emp.empId}</td>
                <td className="border p-2 font-medium text-slate-800">{emp.empName}</td>
                <td className="border p-2 text-gray-600">{emp.empMobileNo || "-"}</td>
                <td className="border p-2 text-purple-700">
                  {restMap[Number(emp.activeRestId) || Number(emp.restId)] || "-"}
                </td>
                <td className="border p-2 text-center text-green-700">
                  {formatDate(emp.joiningDate)}
                </td>
                <td className="border p-2 text-center text-red-600 font-semibold">
                  {formatDate(emp.relievingDate)}
                </td>
                <td className="border p-2 text-center">
                  <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs">
                    {emp.empType || "-"}
                  </span>
                </td>
                <td className="border p-2 text-center">
                  <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded text-xs">
                    {emp.empStatus || "Relieved"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>

        </table>
      </div>

      {/* ── MOBILE CARDS ── */}
      <div className="md:hidden space-y-3 mt-3">
        {loading ? (
          <div className="text-center p-4 text-gray-500">Loading...</div>
        ) : filteredData.length === 0 ? (
          <div className="text-center text-gray-400 py-6">No leaving employees found.</div>
        ) : filteredData.map((emp) => (
          <div key={emp.empId} className="border border-red-200 rounded-xl p-3 shadow-sm bg-red-50">

            <div className="flex justify-between items-start">
              <div>
                <p className="font-semibold text-slate-800">{emp.empName}</p>
                <p className="text-xs text-gray-500">ID: {emp.empId}</p>
              </div>
              <span className="bg-red-100 text-red-700 text-xs px-2 py-0.5 rounded">
                {emp.empStatus || "Relieved"}
              </span>
            </div>

            <div className="mt-2 space-y-1 text-xs text-gray-600">
              <p className="flex items-center gap-1">
                <FaPhone className="text-gray-400" />
                {emp.empMobileNo || "-"}
              </p>
              <p className="flex items-center gap-1 text-purple-700">
                <FaStore className="text-purple-400" />
                {restMap[emp.activeRestId || emp.restId] || "-"}
              </p>
              <p className="flex items-center gap-1 text-green-700">
                <FaCalendarAlt className="text-green-400" />
                Joined: {formatDate(emp.joiningDate)}
              </p>
              <p className="flex items-center gap-1 text-red-600 font-semibold">
                <FaCalendarAlt className="text-red-400" />
                Relieved: {formatDate(emp.relievingDate)}
              </p>
            </div>

          </div>
        ))}
      </div>

    </div>
  );
};

export default LeavingEmployeeReport;